import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Calendar, User, Clock, ArrowLeft, Share2, Tag, ArrowRight } from 'lucide-react';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  useEffect(() => {
    if (article) {
      fetchRelatedArticles();
    }
  }, [article]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/api/blog/articles/${slug}`);
      
      if (!response.ok) {
        throw new Error('Article not found');
      }
      
      const data = await response.json();
      setArticle(data);
    } catch (err) {
      console.error('Error fetching article:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedArticles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/related/${slug}?limit=3`);
      const data = await response.json();
      setRelatedArticles(data || []);
    } catch (error) {
      console.error('Error fetching related articles:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateReadTime = (wordCount) => {
    const wordsPerMinute = 200;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return `${minutes} min read`;
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.meta_description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  // Generate JSON-LD structured data for SEO
  const generateStructuredData = () => {
    if (!article) return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": article.title,
      "description": article.meta_description,
      "image": article.featured_image?.url || "https://www.shippzy.ca/shippzy-logo.png",
      "datePublished": article.published_at,
      "dateModified": article.updated_at,
      "author": {
        "@type": "Organization",
        "name": article.author || "Shippzy Team",
        "url": "https://www.shippzy.ca"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Shippzy",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.shippzy.ca/shippzy-logo.png"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://www.shippzy.ca/blog/${article.slug}`
      },
      "wordCount": article.word_count,
      "keywords": article.focus_keyphrase,
      "articleSection": article.category,
      "inLanguage": article.language || "en"
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8">The article you're looking for doesn't exist or has been removed.</p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  // Sanitize HTML content
  const sanitizedContent = DOMPurify.sanitize(article.content, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'a', 'img', 'code', 'pre',
      'table', 'thead', 'tbody', 'tr', 'th', 'td', 'div', 'span'
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'target', 'rel']
  });

  // JSON-LD Structured Data
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.meta_description,
    image: article.featured_image?.url || 'https://www.shippzy.ca/logo.png',
    datePublished: article.published_at,
    dateModified: article.updated_at,
    author: {
      '@type': 'Organization',
      name: article.author
    },
    publisher: {
      '@type': 'Organization',
      name: 'Shippzy',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.shippzy.ca/logo.png'
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.shippzy.ca/blog/${article.slug}`
    }
  };

  return (
    <>
      <Helmet>
        <title>{`${article.title} | Shippzy Blog`}</title>
        <meta name="description" content={article.meta_description} />
        <link rel="canonical" href={`https://www.shippzy.ca/blog/${article.slug}`} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.meta_description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://www.shippzy.ca/blog/${article.slug}`} />
        {article.featured_image?.url && (
          <meta property="og:image" content={article.featured_image.url} />
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.meta_description} />
        <style>{`
          /* Premium Article Typography */
          .article-content h2 {
            font-weight: 800 !important;
            font-size: 1.875rem !important;
            line-height: 1.2 !important;
            color: #1f2937 !important;
            margin-top: 3rem !important;
            margin-bottom: 1.5rem !important;
            padding-left: 1rem !important;
            border-left: 4px solid #dc2626 !important;
          }
          
          .article-content h3 {
            font-weight: 700 !important;
            font-size: 1.5rem !important;
            line-height: 1.3 !important;
            color: #374151 !important;
            margin-top: 2.5rem !important;
            margin-bottom: 1.25rem !important;
          }
          
          .article-content h4 {
            font-weight: 700 !important;
            font-size: 1.25rem !important;
            color: #4b5563 !important;
            margin-top: 2rem !important;
            margin-bottom: 1rem !important;
          }
          
          .article-content p {
            font-size: 1.125rem !important;
            line-height: 1.8 !important;
            color: #374151 !important;
            margin-bottom: 1.5rem !important;
            margin-top: 0 !important;
          }
          
          .article-content ul,
          .article-content ol {
            margin-top: 2rem !important;
            margin-bottom: 2rem !important;
            padding-left: 1.5rem !important;
          }
          
          .article-content li {
            font-size: 1.125rem !important;
            line-height: 1.75 !important;
            color: #374151 !important;
            margin-bottom: 0.75rem !important;
          }
          
          .article-content strong {
            font-weight: 700 !important;
            color: #111827 !important;
          }
          
          .article-content a {
            color: #dc2626 !important;
            font-weight: 600 !important;
            text-decoration: none !important;
          }
          
          .article-content a:hover {
            text-decoration: underline !important;
          }
          
          @media (max-width: 768px) {
            .article-content h2 {
              font-size: 1.5rem !important;
              margin-top: 2rem !important;
            }
            
            .article-content h3 {
              font-size: 1.25rem !important;
            }
            
            .article-content p,
            .article-content li {
              font-size: 1rem !important;
            }
          }
        `}</style>
      </Helmet>

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <article className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Article Header */}
        <header className="bg-gradient-to-r from-red-600 via-red-700 to-orange-600 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-red-100 hover:text-white mb-6 font-medium transition-colors"
            >
              <ArrowLeft size={20} />
              Back to Blog
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Category Badge */}
              <div className="flex items-center gap-2 mb-4">
                <Tag size={18} />
                <span className="text-lg font-semibold">{article.category}</span>
              </div>

              {/* Title */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                {article.title}
              </h1>

              {/* Meta Information */}
              <div className="flex flex-wrap items-center gap-6 text-red-100">
                <div className="flex items-center gap-2">
                  <User size={18} />
                  <span>{article.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={18} />
                  <span>{formatDate(article.published_at)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={18} />
                  <span>{calculateReadTime(article.word_count)}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </header>

        {/* Featured Image */}
        {article.featured_image?.url && (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="rounded-2xl overflow-hidden shadow-2xl"
            >
              <img
                src={article.featured_image.url}
                alt={article.featured_image.alt || article.title}
                className="w-full h-auto"
              />
            </motion.div>
          </div>
        )}

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white rounded-2xl shadow-xl p-8 md:p-12 lg:p-16 max-w-4xl mx-auto"
          >
            {/* Article Body - Improved Typography & Spacing */}
            <div
              className="article-content prose prose-lg max-w-none
                /* Headings - Bold, Large, Properly Spaced */
                prose-headings:font-extrabold prose-headings:text-gray-900 prose-headings:tracking-tight prose-headings:leading-tight
                prose-h2:text-2xl md:prose-h2:text-3xl lg:prose-h2:text-4xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:text-gray-900 prose-h2:font-extrabold prose-h2:border-l-4 prose-h2:border-red-600 prose-h2:pl-4
                prose-h3:text-xl md:prose-h3:text-2xl lg:prose-h3:text-3xl prose-h3:mt-10 prose-h3:mb-5 prose-h3:text-gray-800 prose-h3:font-bold
                prose-h4:text-lg md:prose-h4:text-xl prose-h4:mt-8 prose-h4:mb-4 prose-h4:text-gray-800 prose-h4:font-bold
                
                /* Paragraphs - Generous Spacing */
                prose-p:text-base md:prose-p:text-lg prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6 prose-p:mt-0
                
                /* Strong/Bold Text */
                prose-strong:text-gray-900 prose-strong:font-bold
                
                /* Lists - Proper Spacing & Formatting */
                prose-ul:my-8 prose-ul:space-y-3 prose-ul:list-disc prose-ul:pl-6
                prose-ol:my-8 prose-ol:space-y-3 prose-ol:list-decimal prose-ol:pl-6
                prose-li:text-base md:prose-li:text-lg prose-li:text-gray-700 prose-li:leading-relaxed prose-li:mb-3
                
                /* Links */
                prose-a:text-red-600 prose-a:no-underline prose-a:font-semibold hover:prose-a:underline hover:prose-a:text-red-700
                
                /* Blockquotes */
                prose-blockquote:border-l-4 prose-blockquote:border-red-600 prose-blockquote:pl-6 prose-blockquote:py-2 prose-blockquote:my-8 prose-blockquote:italic prose-blockquote:text-gray-700 prose-blockquote:bg-red-50
                
                /* Images */
                prose-img:rounded-xl prose-img:shadow-lg prose-img:my-10
                
                /* Code */
                prose-code:text-red-600 prose-code:bg-red-50 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm prose-code:font-medium
                prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:p-6 prose-pre:rounded-xl prose-pre:overflow-x-auto prose-pre:my-8"
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags:</h3>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-red-50 text-red-600 rounded-full text-sm font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Share Button & Back to Blog */}
            <div className="mt-8 pt-8 border-t-2 border-gray-200">
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <button
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  <Share2 size={20} />
                  Share Article
                </button>
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 font-semibold transition-colors"
                >
                  <ArrowLeft size={20} />
                  Back to All Articles
                </Link>
              </div>
            </div>

            {/* Article Footer CTA */}
            <div className="mt-12 pt-12 border-t-2 border-gray-200">
              <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-8 text-center">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Ready to Ship with Shippzy?
                </h3>
                <p className="text-lg text-gray-700 mb-6 max-w-2xl mx-auto">
                  Ship parcels, documents, suitcases, and household belongings across Canada and internationally with Shippzy.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors shadow-lg"
                  >
                    Get a Shipping Quote
                    <ArrowRight size={20} />
                  </Link>
                  <Link
                    to="/schedule-pickup"
                    className="inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors shadow-lg"
                  >
                    Schedule a Pickup
                    <ArrowRight size={20} />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Related Service Pages */}
          {article.related_service_pages && article.related_service_pages.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-12 bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-8"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Related Shippzy Services</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {article.related_service_pages.slice(0, 4).map((pagePath, index) => {
                  const pageNames = {
                    "/shipping-to-india-from-canada": "Shipping to India",
                    "/shipping-canada-to-uk": "Shipping to UK",
                    "/shipping-canada-to-uae": "Shipping to UAE",
                    "/shipping-canada-to-australia": "Shipping to Australia",
                    "/shipping-canada-to-pakistan": "Shipping to Pakistan",
                    "/international-perfume-shipping": "Perfume Shipping",
                    "/international-barrel-shipping": "Barrel Shipping",
                    "/customs-declaration": "Customs Declaration",
                    "/track-shipment": "Track Shipment",
                  };

                  return (
                    <Link
                      key={index}
                      to={pagePath}
                      className="block bg-white hover:bg-red-50 p-4 rounded-lg border-2 border-red-200 hover:border-red-400 transition-all group"
                    >
                      <span className="text-red-600 group-hover:text-red-700 font-semibold">
                        {pageNames[pagePath] || pagePath}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="mt-16"
            >
              <h3 className="text-3xl font-bold text-gray-900 mb-8">Related Articles</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/blog/${related.slug}`}
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow group"
                  >
                    {related.featured_image?.url && (
                      <img
                        src={related.featured_image.url}
                        alt={related.title}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    )}
                    <div className="p-4">
                      <h4 className="font-bold text-gray-900 group-hover:text-red-600 transition-colors line-clamp-2">
                        {related.title}
                      </h4>
                      <p className="text-sm text-gray-600 mt-2 line-clamp-2">{related.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </article>
    </>
  );
};

export default BlogArticlePage;
